import type { AdoptionHealthStatus } from "app/pages/sales-intelligence/modules/extension-management/services/adoptionVariant";
import {
    getAdoptionPercentage,
    getAdoptionStatus,
    getEngagementPercentage,
    getEngagementStatus,
    getTrueHealthPercentage,
    getTrueHealthStatus,
} from "app/pages/sales-intelligence/modules/extension-management/services/adoptionVariant";
import type { ExtensionAdoptionStatsData } from "app/pages/sales-intelligence/modules/extension-management/types";

type StatConfig = {
    labelKey: string;
    getValue: (stats: ExtensionAdoptionStatsData) => number;
};

export type ExtensionAdoptionStatsCardsCardConfig = {
    titleKey: string;
    numerator: StatConfig;
    denominator: StatConfig;
    getPercentage: (stats: ExtensionAdoptionStatsData) => number;
    getStatus: (stats: ExtensionAdoptionStatsData) => AdoptionHealthStatus;
    bottomStats: StatConfig[];
    dataAutomation: string;
};

export const ADOPTION_CARD_CONFIG: ExtensionAdoptionStatsCardsCardConfig = {
    titleKey: "si.management.extension.adoption.card.adoption",
    numerator: { labelKey: "si.management.extension.adoption.stat.installed", getValue: (s) => s.installs },
    denominator: { labelKey: "si.management.extension.adoption.stat.total_seats", getValue: (s) => s.totalSeats },
    getPercentage: getAdoptionPercentage,
    getStatus: getAdoptionStatus,
    bottomStats: [
        { labelKey: "si.management.extension.adoption.stat.total_seats", getValue: (s) => s.totalSeats },
        { labelKey: "si.management.extension.adoption.stat.installed", getValue: (s) => s.installs },
        {
            labelKey: "si.management.extension.adoption.stat.not_installed",
            getValue: (s) => Math.max(0, s.totalSeats - s.installs),
        },
    ],
    dataAutomation: "extension-management-stat-card-adoption",
};

export const ENGAGEMENT_CARD_CONFIG: ExtensionAdoptionStatsCardsCardConfig = {
    titleKey: "si.management.extension.adoption.card.engagement",
    numerator: { labelKey: "si.management.extension.adoption.stat.active", getValue: (s) => s.activeUsers },
    denominator: { labelKey: "si.management.extension.adoption.stat.installed", getValue: (s) => s.installs },
    getPercentage: getEngagementPercentage,
    getStatus: getEngagementStatus,
    bottomStats: [
        { labelKey: "si.management.extension.adoption.stat.installed", getValue: (s) => s.installs },
        { labelKey: "si.management.extension.adoption.stat.active", getValue: (s) => s.activeUsers },
        {
            labelKey: "si.management.extension.adoption.stat.inactive_installed",
            getValue: (s) => Math.max(0, s.installs - s.activeUsers),
        },
    ],
    dataAutomation: "extension-management-stat-card-engagement",
};

export const TRUE_HEALTH_CARD_CONFIG: ExtensionAdoptionStatsCardsCardConfig = {
    titleKey: "si.management.extension.adoption.card.true_health",
    numerator: { labelKey: "si.management.extension.adoption.stat.active", getValue: (s) => s.activeUsers },
    denominator: { labelKey: "si.management.extension.adoption.stat.total_seats", getValue: (s) => s.totalSeats },
    getPercentage: getTrueHealthPercentage,
    getStatus: getTrueHealthStatus,
    bottomStats: [
        { labelKey: "si.management.extension.adoption.stat.total_seats", getValue: (s) => s.totalSeats },
        { labelKey: "si.management.extension.adoption.stat.active_users", getValue: (s) => s.activeUsers },
        {
            labelKey: "si.management.extension.adoption.stat.non_active_seats",
            getValue: (s) => Math.max(0, s.totalSeats - s.activeUsers),
        },
    ],
    dataAutomation: "extension-management-stat-card-true-health",
};

export const EXTENSION_ADOPTION_STATS_CARDS_CONFIG: ExtensionAdoptionStatsCardsCardConfig[] = [
    ADOPTION_CARD_CONFIG,
    ENGAGEMENT_CARD_CONFIG,
    TRUE_HEALTH_CARD_CONFIG,
];
